import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Download, Search } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import {
  clampDateToToday,
  getTodayISODate,
  getYesterdayISODate,
} from '../../utils/dateHelpers';
import { driverApi } from '../Driver/driverApi';
import { getSiteDetails } from '../SiteManagement/siteApi';

const findValueByKeys = (source, keys, visited = new WeakSet()) => {
  if (source === null || typeof source !== 'object') {
    return '';
  }

  if (visited.has(source)) {
    return '';
  }

  visited.add(source);

  if (Array.isArray(source)) {
    for (const item of source) {
      const result = findValueByKeys(item, keys, visited);
      if (result !== '') {
        return result;
      }
    }
    return '';
  }

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  for (const value of Object.values(source)) {
    if (value && typeof value === 'object') {
      const result = findValueByKeys(value, keys, visited);
      if (result !== '') {
        return result;
      }
    }
  }

  return '';
};

const extractRideId = (report = {}) => {
  if (report.ride_id || report.rideId || report.id) {
    return report.ride_id || report.rideId || report.id;
  }

  const fileName = report.file_name || '';
  if (!fileName) return '';

  const uuidMatch = fileName.match(
    /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
  );
  if (uuidMatch) {
    return uuidMatch[0];
  }

  return fileName.replace(/\.pdf$/i, '');
};

const RideReports = () => {
  // Log screen view
  useScreenLogging('RideReports', {
    component: 'RideReports',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => getYesterdayISODate()); // Stores YYYY-MM-DD
  const [allReports, setAllReports] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [rideMetadataMap, setRideMetadataMap] = useState({});
  const [loadingRideMetadata, setLoadingRideMetadata] = useState(false);
  const [rideMetadataError, setRideMetadataError] = useState(null);
  const [siteNameMap, setSiteNameMap] = useState({});
  const [loadingSiteNames, setLoadingSiteNames] = useState(false);
  const [siteNameError, setSiteNameError] = useState(null);
  const siteNameCacheRef = useRef({});

  const BASE_URL = 'https://api.innoworx.ai/Stage'; // From Reports API Documentation

  // Cache configuration
  const CACHE_CONFIG = {
    REPORTS_TTL: 30 * 60 * 1000, // 30 minutes for ride reports
    CACHE_PREFIX: 'rideReports_cache_',
  };

  // Cache utility functions
  const getCacheKey = (type, ...params) => {
    return `${CACHE_CONFIG.CACHE_PREFIX}${type}_${params.join('_')}`;
  };

  const getCachedData = cacheKey => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp, ttl } = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired
      if (now - timestamp > ttl) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      localStorage.removeItem(cacheKey);
      return null;
    }
  };

  const setCachedData = (cacheKey, data, ttl) => {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        ttl,
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error('Error writing to cache:', error);
      // If storage is full, try to clear old cache entries
      clearExpiredCache();
    }
  };

  const clearExpiredCache = () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_CONFIG.CACHE_PREFIX)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const { timestamp, ttl } = JSON.parse(cached);
              if (Date.now() - timestamp > ttl) {
                localStorage.removeItem(key);
              }
            } catch (e) {
              // Invalid cache entry, remove it
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.error('Error clearing expired cache:', error);
    }
  };

  // Cleanup expired cache entries on mount
  useEffect(() => {
    clearExpiredCache();
  }, []);

  // Function to get the authentication token
  const getAuthToken = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      if (tokens && tokens.idToken) {
        return tokens.idToken.toString();
      } else {
        console.error('No ID Token found in the current session.');
        return null;
      }
    } catch (error) {
      console.error('Error fetching authentication session:', error);
      return null;
    }
  };

  const formatDateTime = dateTimeString => {
    if (!dateTimeString) return 'Time unavailable';
    const parsedDate = new Date(dateTimeString);
    if (Number.isNaN(parsedDate.getTime())) {
      return dateTimeString;
    }
    return `${parsedDate.toLocaleDateString()} ${parsedDate.toLocaleTimeString(
      [],
      { hour: '2-digit', minute: '2-digit' }
    )}`;
  };

  const resolveFieldValue = (report, keys = []) => {
    if (!report) return '';
    const directMatch = findValueByKeys(report, keys);
    if (directMatch !== '') {
      return directMatch;
    }
    return '';
  };

  const startSiteKeys = useMemo(
    () => [
      'start_site',
      'startSite',
      'from_site',
      'fromSite',
      'from_site_name',
      'fromSiteName',
    ],
    []
  );
  const endSiteKeys = useMemo(
    () => [
      'end_site',
      'endSite',
      'to_site',
      'toSite',
      'to_site_name',
      'toSiteName',
    ],
    []
  );
  const startTimeKeys = useMemo(
    () => [
      'start_site_time',
      'startSiteTime',
      'start_time',
      'startTime',
      'start_timestamp',
      'startTimestamp',
      'ride_start_timestamp',
      'from_site_time',
      'fromSiteTime',
    ],
    []
  );
  const endTimeKeys = useMemo(
    () => [
      'end_site_time',
      'endSiteTime',
      'end_time',
      'endTime',
      'end_timestamp',
      'endTimestamp',
      'ride_end_timestamp',
      'to_site_time',
      'toSiteTime',
    ],
    []
  );

  useEffect(() => {
    const fetchRideMetadata = async () => {
      if (!selectedDate) {
        setRideMetadataMap({});
        setSiteNameMap({});
        siteNameCacheRef.current = {};
        return;
      }

      setLoadingRideMetadata(true);
      setRideMetadataError(null);

      try {
        siteNameCacheRef.current = {};
        setSiteNameMap({});
        const schedules = await driverApi.fetchDriverSchedulesByDate(
          selectedDate
        );
        const metadata = {};
        (schedules || []).forEach(ride => {
          const rideId = ride?.ride_id || ride?.rideId || ride?.id;
          if (rideId) {
            metadata[rideId] = ride;
          }
        });
        setRideMetadataMap(metadata);
        
        // Log API event for fetching ride metadata
        loggingService.logInfo(
          'api_success_ride_reports',
          `Successfully fetched ride metadata for date ${selectedDate}`,
          {
            endpoint: 'driverApi.fetchDriverSchedulesByDate',
            method: 'GET',
            date: selectedDate,
            schedulesCount: schedules?.length || 0,
          }
        ).catch(logError => {
          console.warn('[RideReports] Failed to log event:', logError);
        });
      } catch (err) {
        console.error(
          'Error fetching driver schedules for ride metadata:',
          err
        );
        setRideMetadataMap({});
        setRideMetadataError(err?.message || 'Failed to load ride metadata.');
      } finally {
        setLoadingRideMetadata(false);
      }
    };

    fetchRideMetadata();
  }, [selectedDate]);

  useEffect(() => {
    const loadSiteNames = async () => {
      const siteIds = new Set();
      Object.values(rideMetadataMap || {}).forEach(ride => {
        if (ride?.from_site_id) {
          siteIds.add(ride.from_site_id);
        }
        if (ride?.to_site_id) {
          siteIds.add(ride.to_site_id);
        }
      });

      const missingSiteIds = Array.from(siteIds).filter(
        id => id && !siteNameCacheRef.current[id]
      );

      if (missingSiteIds.length === 0) {
        return;
      }

      setLoadingSiteNames(true);
      setSiteNameError(null);

      try {
        const results = await Promise.all(
          missingSiteIds.map(async siteId => {
            try {
              const siteDetails = await getSiteDetails(siteId);
              const name =
                siteDetails?.site_name ??
                siteDetails?.siteName ??
                siteDetails?.site?.site_name ??
                siteDetails?.site?.name ??
                siteDetails?.data?.site_name ??
                siteDetails?.data?.site?.site_name ??
                siteDetails?.name ??
                'Unknown';

              return {
                siteId,
                name,
              };
            } catch (err) {
              console.error(`Error fetching site details for ${siteId}:`, err);
              return { siteId, name: 'Unknown' };
            }
          })
        );

        setSiteNameMap(prev => {
          const next = { ...prev };
          results.forEach(({ siteId, name }) => {
            siteNameCacheRef.current[siteId] = name;
            next[siteId] = name;
          });
          return next;
        });
      } catch (err) {
        console.error('Error loading site names for ride metadata:', err);
        setSiteNameError(err?.message || 'Failed to load site names.');
      } finally {
        setLoadingSiteNames(false);
      }
    };

    loadSiteNames();
  }, [rideMetadataMap]);

  // Function to fetch reports from the API
  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedDate) {
        setAllReports([]);
        setReports([]);
        return;
      }

      setLoadingReports(true);
      setError(null);

      // Check cache first
      const cacheKey = getCacheKey('date', selectedDate);
      const cachedReports = getCachedData(cacheKey);

      if (cachedReports !== null) {
        // null means no cache, but empty array is valid cached data (no reports found)
        setAllReports(cachedReports);
        setLoadingReports(false);
        setError(null);
        return;
      }

      try {
        const token = await getAuthToken();
        if (!token) {
          setError(
            'Authentication token is missing. Please ensure a user is logged in.'
          );
          setLoadingReports(false);
          return;
        }

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        // API endpoint for ride reports expects a YYYY-MM-DD date
        const apiUrl = `${BASE_URL}/report/ride?date=${selectedDate}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers,
        });

        if (response.status === 404) {
          const emptyReports = [];
          setAllReports(emptyReports);
          // Cache the empty result as well
          setCachedData(cacheKey, emptyReports, CACHE_CONFIG.REPORTS_TTL);
          setLoadingReports(false);
          setError(null);
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorText}`
          );
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          console.warn('API response for ride reports is not an array:', data);
          setError(
            'Received unexpected data format from API. Please check console for details.'
          );
          setAllReports([]);
          setReports([]);
          setLoadingReports(false);
          return;
        }

        setAllReports(data);

        // Cache the results
        setCachedData(cacheKey, data, CACHE_CONFIG.REPORTS_TTL);
        
        // Log API event for viewing ride report
        loggingService.logInfo(
          'report_view_ride',
          `User viewed ride report for date ${selectedDate}`,
          {
            endpoint: '/report/ride',
            method: 'GET',
            date: selectedDate,
            reportCount: data.length,
          }
        ).catch(logError => {
          console.warn('[RideReports] Failed to log event:', logError);
        });
      } catch (err) {
        console.error('Error fetching ride reports:', err);
        setError(
          `Failed to load ride reports. Please try again. Error: ${err.message}`
        );
        setAllReports([]);
        setReports([]);
      } finally {
        setLoadingReports(false);
      }
    };

    if (BASE_URL !== 'YOUR_API_BASE_URL') {
      const timer = setTimeout(() => {
        fetchReports();
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setError('API Base URL is not configured. Please update BASE_URL.');
      setLoadingReports(false);
      setReports([]);
    }
  }, [selectedDate, BASE_URL]);

  // Filter reports based on search term
  useEffect(() => {
    const filteredReports = allReports.filter(report => {
      const fileName = report.file_name ? report.file_name.toLowerCase() : '';
      return fileName.includes(searchTerm.toLowerCase());
    });

    setReports(filteredReports);
  }, [searchTerm, allReports]);

  // Note: PDF photo angle/view orientation is controlled by the backend PDF generation service.
  // To change the photo view angle in the generated PDF, modifications need to be made to the
  // Lambda function that generates the ride report PDF (likely rakshak-ride-report-generator-pdfs).
  // The frontend cannot control the photo orientation in the generated PDF.
  const handleDownload = reportUrl => {
    if (!reportUrl) {
      setError('No download URL available for this ride report.');
      return;
    }

    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col flex-1">
      <div className="self-start mb-6">
        <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">
          Ride Reports
        </h1>
      </div>

      <div className="w-full max-w-xs">
        <input
          type="date"
          className={`mb-3 block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug ${
            selectedDate
              ? 'outline-green-700 text-green-700'
              : 'outline-white text-neutral-900'
          }`}
          value={selectedDate}
          max={getTodayISODate()}
          onChange={e => {
            const safeValue =
              clampDateToToday(e.target.value) || getYesterdayISODate();
            setSelectedDate(safeValue);
            setAllReports([]);
            setReports([]);
          }}
        />
      </div>

      <div className="inline-flex gap-2.5">
        <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-10">
          Reports
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-start gap-4 mb-6">
        <div className="w-full lg:max-w-xl">
          <div className="h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5">
            <div className="inline-flex justify-start items-center gap-4 w-full">
              <Search className="w-6 h-6 text-neutral-400" />
              <input
                type="text"
                placeholder="Search Ride"
                className="flex-grow bg-transparent outline-none text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug placeholder-neutral-400"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {rideMetadataError && (
        <div className="mb-4 w-full rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Unable to load ride metadata for the selected date. Some site/time
          details may be unavailable. Error: {rideMetadataError}
        </div>
      )}

      {siteNameError && (
        <div className="mb-4 w-full rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Unable to resolve some site names. They may appear as "Site
          unavailable". Error: {siteNameError}
        </div>
      )}

      <div className="flex-1 inline-flex justify-start items-start gap-4 flex-wrap content-start overflow-y-auto pr-4">
        {loadingReports ? (
          <div className="flex items-center justify-center w-full h-full text-gray-500">
            Loading ride reports...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center w-full h-full text-red-500">
            {error}
          </div>
        ) : reports.length > 0 ? (
          reports.map((report, index) => {
            const rideId = extractRideId(report);
            const metadata = rideId ? rideMetadataMap[rideId] : null;
            const metadataStartSite = metadata
              ? findValueByKeys(metadata, startSiteKeys)
              : '';
            const metadataEndSite = metadata
              ? findValueByKeys(metadata, endSiteKeys)
              : '';
            const metadataStartTime = metadata
              ? findValueByKeys(metadata, startTimeKeys)
              : '';
            const metadataEndTime = metadata
              ? findValueByKeys(metadata, endTimeKeys)
              : '';

            const startSite =
              metadataStartSite ||
              (metadata?.from_site_id
                ? siteNameMap[metadata.from_site_id]
                : '') ||
              resolveFieldValue(report, startSiteKeys);
            const endSite =
              metadataEndSite ||
              (metadata?.to_site_id ? siteNameMap[metadata.to_site_id] : '') ||
              resolveFieldValue(report, endSiteKeys);
            const startTimeRaw =
              metadataStartTime || resolveFieldValue(report, startTimeKeys);
            const endTimeRaw =
              metadataEndTime || resolveFieldValue(report, endTimeKeys);

            const titleSegments = [];
            if (startSite && startSite !== 'Site unavailable') {
              titleSegments.push(startSite);
            }
            if (endSite && endSite !== 'Site unavailable') {
              titleSegments.push(endSite);
            }

            const displayTitle =
              titleSegments.length > 0
                ? titleSegments.join(' \u2192 ')
                : 'Ride Report';

            const handleCardActivate = () => handleDownload(report.url);

            return (
              <div
                key={report.url || index}
                className="self-stretch inline-flex justify-between items-center w-full p-4 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-white backdrop-blur-[20px] cursor-pointer transition-shadow hover:shadow-lg"
                role="button"
                tabIndex={0}
                onClick={handleCardActivate}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCardActivate();
                  }
                }}
              >
                <div className="flex flex-col gap-2 w-full">
                  <span className="text-black text-lg font-semibold font-['Open_Sans'] leading-6">
                    {displayTitle}
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-neutral-600">
                    <div>
                      <p className="font-semibold text-neutral-900">
                        Start Site
                      </p>
                      <p className="text-neutral-700">
                        {startSite || 'Site unavailable'}
                      </p>
                      <p className="font-semibold text-neutral-900 mt-2">
                        Start Time
                      </p>
                      <p className="text-neutral-700">
                        {formatDateTime(startTimeRaw)}
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">End Site</p>
                      <p className="text-neutral-700">
                        {endSite || 'Site unavailable'}
                      </p>
                      <p className="font-semibold text-neutral-900 mt-2">
                        End Time
                      </p>
                      <p className="text-neutral-700">
                        {formatDateTime(endTimeRaw)}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                  onClick={event => {
                    event.stopPropagation();
                    handleCardActivate();
                  }}
                >
                  <Download
                    className="w-6 h-6 text-white stroke-white"
                    strokeWidth={2}
                  />
                </button>
              </div>
            );
          })
        ) : (
          <div className="flex items-center justify-center w-full h-full text-gray-500">
            {!selectedDate
              ? 'Please select a date.'
              : 'No reports found for the selected criteria.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideReports;
